import React from 'react';
import { useParams } from 'react-router-dom';
import { useSparkStepsBySparkID } from 'hooks/SparkBuilder/sparkStep';
import {
  InfoCard,
  SparkDataSearchBar,
  SparkImageLoader,
} from 'sharedComponents/UI';
import { Box, Grid, Typography } from '@mui/material';
import { useSparkStepTypes } from 'hooks/EntityTypes/useSparkStepTypes';
import { useBoardNodeTypes } from 'hooks/EntityTypes/useBoardNodeTypes';
import { useAddBoardNode } from 'hooks/Board';
import { StepTest } from 'types/SparkStep';
import { parseJSONCleaned } from 'utilities/index';

type Props = {
  boardID: string | null | undefined;
  onClose: () => void;
};

function LinkToStepView({ boardID, onClose }: Props) {
  const [query, setQuery] = React.useState('');
  const { id: sparkID = null } = useParams();
  const { idMap } = useSparkStepTypes();
  const { idMap: boardNodeTypesIdMap } = useBoardNodeTypes();
  const { mutateAsync: addBoardNode, isLoading: isAddingBoardNode } =
    useAddBoardNode(boardID);

  const { sparkSteps, promptFunctionsByIdMap } =
    useSparkStepsBySparkID(sparkID);

  const filteredSparkSteps = React.useMemo(() => {
    if (!sparkSteps) {
      return [];
    }
    return sparkSteps.filter((step) =>
      step.attributes.name.toLowerCase().includes(query.toLowerCase())
    );
  }, [sparkSteps, query]);

  const getOutputType = (promptFunctionID: string | null) => {
    if (!promptFunctionID) {
      return null;
    }
    const promptFunction = promptFunctionsByIdMap?.[promptFunctionID];
    if (!promptFunction) {
      return null;
    }
    return promptFunction.attributes.outputBoardNodeTypeID;
  };

  const handleAddNode = async (
    stepID: string,
    outputTypeID: string | null | undefined
  ) => {
    if (!outputTypeID || isAddingBoardNode) {
      return;
    }
    const sparkStep = sparkSteps?.find((step) => step.id === stepID);
    const sparkTestResults = parseJSONCleaned<StepTest>(
      sparkStep?.attributes.testResults || ''
    );
    await addBoardNode({
      sparkStepID: stepID,
      boardNodeTypeID: +outputTypeID,
      data: sparkTestResults?.SparkTask.Output || '',
    });
    onClose();
  };

  const contentAvailable = React.useMemo(() => {
    return !!filteredSparkSteps?.length;
  }, [filteredSparkSteps?.length]);

  return (
    <Box>
      <SparkDataSearchBar
        setQuery={setQuery}
        placeholder='Search Steps'
        sx={{ width: '100%' }}
      />
      {!contentAvailable && (
        <InfoCard info={`No steps found for ${query}`} type='error' />
      )}
      {contentAvailable && (
        <Grid container direction='column' gap={2}>
          {filteredSparkSteps?.map((step) => (
            <Grid
              onClick={() =>
                handleAddNode(
                  step.id,
                  getOutputType(step.attributes.promptFunctionID)
                )
              }
              container
              item
              width='100%'
              border={(theme) => `1px solid ${theme.palette.borderColor}`}
              borderRadius='10px'
              key={step.id}
              padding={2}
              justifyContent='space-between'
              sx={{
                cursor: isAddingBoardNode ? 'not-allowed' : 'pointer',
                opacity: isAddingBoardNode ? 0.5 : 1,
                '&:hover': {
                  borderColor: (theme) =>
                    isAddingBoardNode
                      ? theme.palette.borderColor
                      : theme.palette.primary.main,
                },
              }}
            >
              <Box display='flex' alignItems='center' gap={2}>
                <Box
                  borderRadius='10px'
                  sx={{ aspectRatio: 1 }}
                  width='40px'
                  bgcolor={(theme) => theme.palette.primary.light}
                  display='flex'
                  justifyContent='center'
                  alignItems='center'
                >
                  <Typography
                    variant='body1'
                    sx={{ opacity: isAddingBoardNode ? 0.5 : 1 }}
                  >
                    {step.attributes.stepOrder}
                  </Typography>
                </Box>
                <SparkImageLoader
                  width='30px'
                  height='30px'
                  style={{
                    borderRadius: '12px',
                    opacity: isAddingBoardNode ? 0.5 : 1,
                  }}
                  src={
                    idMap?.[step.attributes.sparkStepTypeID]?.attributes.iconURL
                  }
                  alt=''
                />
                <Typography
                  variant='body1'
                  sx={{ opacity: isAddingBoardNode ? 0.5 : 1 }}
                >
                  {step.attributes.name}
                </Typography>
              </Box>
              <Box
                display='flex'
                alignItems='center'
                gap={1}
                sx={{
                  minWidth: '110px',
                }}
              >
                <SparkImageLoader
                  width='25px'
                  height='25px'
                  style={{
                    opacity: isAddingBoardNode ? 0.5 : 1,
                  }}
                  src={
                    boardNodeTypesIdMap?.[
                      getOutputType(step.attributes.promptFunctionID) || ''
                    ]?.attributes.iconURL || ''
                  }
                  alt=''
                />
                <Typography
                  variant='body1'
                  sx={{ opacity: isAddingBoardNode ? 0.5 : 1 }}
                >
                  {boardNodeTypesIdMap?.[
                    getOutputType(step.attributes.promptFunctionID) || ''
                  ]?.attributes.name || ''}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
}

export default LinkToStepView;
